import { type JSX, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { getOrganizations } from 'Api/UcrmTprm/UcrmTprmApi';
import { Button, Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { ControlTable } from 'Components/ControlTable/ControlTable';
import { IssueHistoryTable, IssueHistoryTableProps } from 'Components/Issues/IssueHistoryTable/IssueHistoryTable';
import { AssociatedControlsModal } from 'Components/Modal/AssociatedControlsModal/AssociatedControlsModal';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { PrimaryTabs, Tab } from 'Components/Tabs/PrimaryTabs/PrimaryTabs';
import { Text } from 'Components/Text/Text';
import { OrganizationSummary } from 'Components/UcrmTprm/OrganizationSummary/OrganizationSummary';
import { ICON_DOWNLOAD, ICON_EDIT_MODIFY_UPDATE } from 'Config/Icons';
import { ISSUES, ISSUES_EXCEPTIONS } from 'Config/Paths';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { downloadElementAsPdf } from 'Helpers/PdfUtils';
import { getUpdateIssueUrl, pathPrefixForIssuesExceptionsModule } from 'Helpers/URLBuilder/URLBuilder';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { IssueHistoryResponse, IssueResponse, IssueStatus } from 'Models/Issues';
import { OperationalControl } from 'Models/OperationalControls';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { ThirdPartyResponseWithServices } from 'Models/TPRM';
import { BusinessResponseWithUseCases } from 'Models/Ucrm';
import styles from 'Styles/DetailsPage.module.css';

import { IssueDetailsSnapshot } from '../Components/IssueDetailsSnapshot/IssueDetailsSnapshot';
import { IssueHistoryModal } from '../Components/IssueHistoryModal/IssueHistoryModal';

enum IssueTab {
    CONTROLS = 'Controls',
    ORGANIZATION = 'Third Party',
    HISTORY = 'History',
}

interface UrlParams {
    issueId: string;
}

interface IssueDetailsOperationalControlsProps {
    authHandler: AuthHandler;
    type: ProductModule.CONTROLS;
    issuesApi: IssuesApi;
    documentApi: DocumentApi;
    moduleApi?: never; // Typed like this so that `moduleApi` can be used unconditionally as a `useEffect` dependency.
}

interface IssueDetailsDueDiligenceProps {
    authHandler: AuthHandler;
    type: DueDiligenceModule;
    issuesApi: IssuesApi;
    documentApi: DocumentApi;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
}

export type IssueDetailsProps = IssueDetailsOperationalControlsProps | IssueDetailsDueDiligenceProps;

/**
 * Renders a page of detailed information for a specific issue.
 * This includes: current details; a tab containing a table of historical snapshots; and, depending on the issue type, a tab containing either a table of impacted controls or a summary of the impacted third party.
 */
export const IssueDetails = (props: IssueDetailsProps) => {
    const cachedData = useCachedData();
    const { issueId } = useParams<keyof UrlParams>() as UrlParams;
    const printPdfRef = useRef<HTMLDivElement>(null);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [issue, setIssue] = useState<IssueResponse>();
    const [organizationIdToOrganizationMap, setOrganizationIdToOrganizationMap] = useState<Map<string, ThirdPartyResponseWithServices | BusinessResponseWithUseCases>>(); // This isn't actually used when the issue belongs to the Operational Controls module.
    const [issueHistory, setIssueHistory] = useState<IssueHistoryResponse[]>();
    const [issueHistoryToShowInModal, setIssueHistoryToShowInModal] = useState<IssueHistoryResponse>();
    const [impactedControlsToShow, setImpactedControlsToShow] = useState<OperationalControl[]>();

    useEffect(() => {
        const getIssueDetails = async (issueId: string): Promise<void> => {
            try {
                const getIssueDetailsResponse = await props.issuesApi.getIssue(issueId);
                const issue = getIssueDetailsResponse.data;
                setIssue(issue);
            } catch (error) {
                handleRequestError(error);
            }
        };

        getIssueDetails(issueId);
    }, [issueId, props.issuesApi]);

    useEffect(() => {
        const getIssueHistory = async (issueId: string): Promise<void> => {
            try {
                const response = await props.issuesApi.getIssueHistory(issueId);
                setIssueHistory(response.data);
            } catch (error) {
                handleRequestError(error);
            }
        };

        getIssueHistory(issueId);
    }, [issueId, props.issuesApi]);

    useEffect(() => {
        const fetchOrganizations = async (): Promise<void> => {
            if (props.type === ProductModule.CONTROLS) {
                setOrganizationIdToOrganizationMap(new Map());
                return;
            }

            try {
                // TODO: `getOrganizationDetails` should be used if/when `ThirdPartyResponseWithServices`/`BusinessResponseWithUseCases` and `OrganizationResponseBase` are consolidated.
                const response = await getOrganizations(props.moduleApi);
                setOrganizationIdToOrganizationMap(new Map(response.data.map((organization) => [organization.id, organization])));
            } catch (error) {
                handleRequestError(error);
            }
        };

        fetchOrganizations();
    }, [props.moduleApi, props.type]);

    const handleRequestError = (error: Error): void => setErrorMessage(error.message);

    if (errorMessage) {
        return <Text>{errorMessage}</Text>;
    }

    if (!(issue && issueHistory && organizationIdToOrganizationMap)) {
        return <Placeholder />;
    }

    /** Convert a component to a PDF and download it. */
    const handleDownloadPdf = async () => {
        const elementToPrint = printPdfRef.current;
        if (!elementToPrint) {
            return;
        }

        let filename;
        if (issue.impacted_vendor) {
            filename = `${organizationIdToOrganizationMap.get(issue.impacted_vendor!)!.name} - ${issue.title}.pdf`;
        } else {
            filename = `${issue.title}.pdf`;
        }

        const loggedInUser = props.authHandler.getAuthenticatedUserFullName(cachedData.users);

        await downloadElementAsPdf(elementToPrint, filename, loggedInUser);
    };

    const buttons: JSX.Element = (() => {
        switch (issue.status) {
            case IssueStatus.DRAFT_OPEN:
                return (
                    <>
                        <Button variant="secondary" onClick={handleDownloadPdf} fontAwesomeImage={ICON_DOWNLOAD}>
                            Download
                        </Button>
                        <Link variant="primaryButton" to={getUpdateIssueUrl(issue.id, issue.type, false)} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                            Manage Issue
                        </Link>
                    </>
                );
            case IssueStatus.OPEN:
                return (
                    <>
                        <Button variant="secondary" onClick={handleDownloadPdf} fontAwesomeImage={ICON_DOWNLOAD}>
                            Download
                        </Button>
                        <Link variant="secondaryButton" to={getUpdateIssueUrl(issue.id, issue.type, false)} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                            Manage Issue
                        </Link>
                        <Link variant="primaryButton" to={getUpdateIssueUrl(issue.id, issue.type, true)} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                            Close Issue
                        </Link>
                    </>
                );
            case IssueStatus.CLOSED:
                return (
                    <Button variant="secondary" onClick={handleDownloadPdf} fontAwesomeImage={ICON_DOWNLOAD}>
                        Download
                    </Button>
                );
        }
    })();

    const getOrganizationName = (organizationId: string) => organizationIdToOrganizationMap.get(organizationId)!.name;

    const issueHistoryTableProps: IssueHistoryTableProps = (() => {
        switch (issue.type) {
            case ProductModule.CONTROLS:
                return { type: ProductModule.CONTROLS, histories: issueHistory, displayMappedControlsModal: setImpactedControlsToShow, onSelectHistory: (history: IssueHistoryResponse) => setIssueHistoryToShowInModal(history) };
            case ProductModule.TPRM:
            case ProductModule.UCRM:
                return { type: issue.type, histories: issueHistory, getOrganizationName: getOrganizationName, onSelectHistory: (history: IssueHistoryResponse) => setIssueHistoryToShowInModal(history) };
        }
    })();

    return (
        <>
            {issueHistoryToShowInModal && <IssueHistoryModal history={issueHistoryToShowInModal} documentApi={props.documentApi} hideModal={() => setIssueHistoryToShowInModal(undefined)} />}
            {impactedControlsToShow && <AssociatedControlsModal associatedControls={impactedControlsToShow} hideModal={() => setImpactedControlsToShow(undefined)} />}
            <div ref={printPdfRef}>
                <PageLayoutDefault
                    headerBreadcrumb={
                        <Breadcrumb textColor="blue">
                            <BreadcrumbLink link={`${pathPrefixForIssuesExceptionsModule(issue.type)}/${ISSUES_EXCEPTIONS}#${ISSUES}`}>Issues</BreadcrumbLink>
                            <BreadcrumbText>{issue.title}</BreadcrumbText>
                        </Breadcrumb>
                    }
                    headerTitle={issue.title}
                    headerButtons={buttons}
                    headerDescription={issue.status === IssueStatus.CLOSED ? `Closed: ${iso8601ToUsDateShort(issue.closed_timestamp!)} by ${getUserNameFromSubject(issue.closed_by!, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}` : `Last updated: ${iso8601ToUsDateShort(issue.last_updated_timestamp)} by ${getUserNameFromSubject(issue.last_updated_by, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}`}
                    body={[
                        {
                            title: 'Issue Details',
                            content: <IssueDetailsSnapshot documentApi={props.documentApi} issueResponse={issue} type="current" />,
                        },
                        {
                            content: (
                                <PrimaryTabs defaultActiveTab={issue.type === ProductModule.CONTROLS ? IssueTab.CONTROLS : IssueTab.ORGANIZATION} removePadding transparent>
                                    {issue.type === ProductModule.CONTROLS ? (
                                        <Tab eventKey={IssueTab.CONTROLS} title={IssueTab.CONTROLS}>
                                            <div className={styles.tabContent}>
                                                <ControlTable controls={issue.impacted_controls} />
                                            </div>
                                        </Tab>
                                    ) : (
                                        <Tab eventKey={IssueTab.ORGANIZATION} title={issue.type === ProductModule.UCRM ? 'Business' : IssueTab.ORGANIZATION}>
                                            <div className={styles.tabContent}>
                                                <Text variant="Header2" color="blue" noStyles>
                                                    {organizationIdToOrganizationMap.get(issue.impacted_vendor!)!.name}
                                                </Text>
                                                <hr />
                                                <OrganizationSummary module={issue.type} organization={organizationIdToOrganizationMap.get(issue.impacted_vendor!)!} />
                                            </div>
                                        </Tab>
                                    )}
                                    <Tab eventKey={IssueTab.HISTORY} title={IssueTab.HISTORY}>
                                        <div className={styles.tabContent} data-testid="issueHistoryTable">
                                            {issue.status !== IssueStatus.DRAFT_OPEN ? <IssueHistoryTable {...issueHistoryTableProps} /> : <Text>History will not be recorded until the issue is opened.</Text>}
                                        </div>
                                    </Tab>
                                </PrimaryTabs>
                            ),
                        },
                    ]}
                />
            </div>
        </>
    );
};
